/**
 * Stytch Discovery and Organization Management Hooks
 *
 * These hooks provide convenient wrappers for discovery and organization
 * management operations using the Stytch B2B headless client.
 */
//import { useStytchB2BClient } from './client'
import { getStytchClient } from './../client/headless-client';
import { getIntermediateSessionToken } from './../browser-utils';
import { STYTCH_DEFAULT_SESSION_DURATION_MINUTES } from '../../../core/session-durations';
/**
 * Hook to exchange intermediate session for organization
 */
export function useExchangeIntermediateSession() {
    return async (organizationId, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES, locale) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            // The headless client should manage the IST via cookie automatically after
            // oauth.discovery.authenticate(). However, page navigation (callback → workspace
            // selection) can lose that in-memory state. We explicitly read the token from
            // sessionStorage (where the callback page stores it) and pass it directly to
            // ensure the exchange works even if the SDK cookie is missing or expired.
            const intermediateSessionToken = getIntermediateSessionToken();
            const result = await stytch.discovery.intermediateSessions.exchange({
                organization_id: organizationId,
                session_duration_minutes: sessionDurationMinutes,
                locale: locale,
                ...(intermediateSessionToken ? { intermediate_session_token: intermediateSessionToken } : {}),
            });
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                memberId: result.member_id,
                sessionJwt: result.session_jwt,
                sessionToken: result.session_token,
                intermediateSessionToken: result.intermediate_session_token,
                memberSession: result.member_session,
                memberAuthenticated: result.member_authenticated,
                primaryRequired: result.primary_required,
                mfaRequired: result.mfa_required,
                organization: result.organization,
                member: result.member,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to exchange session',
                data: error,
            };
        }
    };
}
/**
 * Hook to list discovered organizations.
 *
 * Works for both pre-auth (intermediate session) and fully-authenticated callers:
 * per Stytch's docs, `discovery.organizations.list()` uses the current Member Session
 * token when one exists, falling back to the intermediate session token otherwise, and
 * consumes neither. See https://stytch.com/docs/api-reference/b2b/frontend-sdks/react/methods/discovery/list-discovered-organizations
 */
export function useListDiscoveredOrganizations() {
    return async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.discovery.organizations.list();
            return {
                success: true,
                email_address: result.email_address,
                discovered_organizations: result.discovered_organizations,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to list discovered organizations',
                data: error,
            };
        }
    };
}
/**
 * Hook to create organization via discovery
 */
export function useCreateOrganizationViaDiscovery() {
    return async (organizationName, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES, options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            // Same IST passthrough fix as useExchangeIntermediateSession — page navigation
            // (OAuth callback → workspace selection) can lose the SDK's in-memory IST state.
            const intermediateSessionToken = getIntermediateSessionToken();
            const result = await stytch.discovery.organizations.create({
                organization_name: organizationName,
                organization_slug: options?.organizationSlug || organizationName.toLowerCase().replace(/\s+/g, '-'),
                session_duration_minutes: sessionDurationMinutes,
                organization_logo_url: options?.organizationLogoUrl,
                email_jit_provisioning: options?.emailJitProvisioning,
                email_allowed_domains: options?.emailAllowedDomains,
                sso_jit_provisioning: options?.ssoJitProvisioning,
                auth_methods: options?.authMethods,
                allowed_auth_methods: options?.allowedAuthMethods,
                mfa_policy: options?.mfaPolicy,
                ...(intermediateSessionToken ? { intermediate_session_token: intermediateSessionToken } : {}),
            });
            return {
                success: true,
                member_session: result.member_session,
                organization: result.organization,
                member: result.member,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to create organization',
                data: error,
            };
        }
    };
}
